import { type FC, useMemo } from 'react';

import { Tag } from '@wk/components-react16';

import type {
  SiteComplianceConfiguration,
  SiteComplianceSite,
} from '@/domains/siteCompliance';
import {
  SITE_COMPLIANCE_NETWORK_TIER_LABELS,
  SITE_COMPLIANCE_SITE_TYPE_LABELS,
  normalizeSiteComplianceDivision,
} from '@/domains/siteCompliance';

interface SiteComplianceOnboardDetailsProps {
  latestConfiguration: SiteComplianceConfiguration | null;
  site: SiteComplianceSite | null;
}

const formatSiteDate = (date: string | null) => {
  if (!date) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(date));
};

const SiteComplianceOnboardDetails: FC<SiteComplianceOnboardDetailsProps> = ({
  latestConfiguration,
  site,
}) => {
  const checkpointSummary = useMemo(() => {
    if (!latestConfiguration) {
      return {
        exceptions: 0,
        preview: 0,
        scored: 0,
        total: 0,
      };
    }

    return latestConfiguration.checkpoints.reduce(
      (summary, checkpoint) => ({
        exceptions: checkpoint.exceptions?.length
          ? summary.exceptions + 1
          : summary.exceptions,
        preview: checkpoint.isScored ? summary.preview : summary.preview + 1,
        scored: checkpoint.isScored ? summary.scored + 1 : summary.scored,
        total: summary.total + 1,
      }),
      {
        exceptions: 0,
        preview: 0,
        scored: 0,
        total: 0,
      },
    );
  }, [latestConfiguration]);

  if (!site) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 border-b border-gray-400 pb-6 max-sm:pb-4">
      <h3 className="text-lg font-medium">Site Details</h3>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:gap-x-20 sm:gap-y-3">
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Site ID</span>
          <span className="text-xs">{site.siteId}</span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Workday ID</span>
          <span className="text-xs" title={site.workdayId ? undefined : 'Field not provided by Inventory'}>
            {site.workdayId ?? '—'}
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Name</span>
          <span className="text-xs flex flex-wrap items-center gap-2">
            {site.siteName}
            <Tag color="blue">
              {SITE_COMPLIANCE_SITE_TYPE_LABELS[site.siteType]}
            </Tag>
            {site.networkTier ? (
              <Tag color="green">
                {SITE_COMPLIANCE_NETWORK_TIER_LABELS[site.networkTier]}
              </Tag>
            ) : null}
            {site.isFlagship ? <Tag color="black">Flagship</Tag> : null}
            {site.isEnhanced ? <Tag color="gray">Enhanced</Tag> : null}
            {site.isDatacenter ? <Tag color="orange">Datacenter</Tag> : null}
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Primary Division</span>
          <span className="text-xs">
            {site.primaryDivisions.length
              ? site.primaryDivisions
                  .map(normalizeSiteComplianceDivision)
                  .join(', ')
              : 'N/A'}
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Business Unit</span>
          <span className="text-xs">
            {site.businessUnits.length ? site.businessUnits.join(', ') : 'N/A'}
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Region / Country</span>
          <span className="text-xs">
            {site.region}
            {site.country ? ` / ${site.country}` : ''}
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Last Reviewed</span>
          <span className="text-xs">{formatSiteDate(site.lastReviewedAt)}</span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Published Checkpoints</span>
          <span className="text-xs">
            {checkpointSummary.total} total / {checkpointSummary.scored} scored
          </span>
        </div>
        <div className="flex min-w-0 flex-col sm:w-[300px]">
          <span className="text-xs font-medium">Preview / Exceptions</span>
          <span className="text-xs">
            {checkpointSummary.preview} preview / {checkpointSummary.exceptions}{' '}
            exceptions
          </span>
        </div>
        <div className="col-span-2 flex min-w-0 flex-col sm:w-[300px] sm:flex-1">
          <span className="text-xs font-medium">Address</span>
          <span className="text-xs">
            {site.address ?? 'Physical address not available.'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SiteComplianceOnboardDetails;
